import { useMemo, useState } from 'react';
import { ShieldCheck, Plus, MoreHorizontal, Search } from 'lucide-react';
import { PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  IconButton,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useOrgAdmins, useRemoveAdmin, orgErrorMessage, type OrgAdmin } from '@/api/organization';
import { adminRoleTone, titleCase } from '../shared';
import { AddAdminDialog } from './AddAdminDialog';

interface AdminsTabProps {
  canManage: boolean;
  /** The current user's login id — primary key for highlighting their own row. */
  selfUserUuid?: string;
  /** Fallback for self-detection when `selfUserUuid` is empty. */
  selfContactId?: number;
}

export function AdminsTab({ canManage, selfUserUuid, selfContactId }: AdminsTabProps) {
  const { show } = useToast();
  const { data: admins = [], isLoading, isError } = useOrgAdmins();
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState<OrgAdmin | null>(null);
  const [search, setSearch] = useState('');

  // Admin lists are small and non-paginated — filter client-side.
  const q = search.trim().toLowerCase();
  const rows = q
    ? admins.filter((a) =>
        [a.contact_name, a.contact_email, a.role].some((v) => v?.toLowerCase().includes(q)),
      )
    : admins;

  const remove = useRemoveAdmin({
    onSuccess: () => {
      show({ tone: 'success', title: 'Administrator removed' });
      setRemoving(null);
    },
    onError: (err) =>
      show({ tone: 'danger', title: orgErrorMessage(err, 'Could not remove administrator') }),
  });

  const isLastAdmin = admins.length <= 1;

  const columns = useMemo<ColumnDef<OrgAdmin, unknown>[]>(() => {
    const cols: ColumnDef<OrgAdmin, unknown>[] = [
      {
        id: 'name',
        header: 'Administrator',
        cell: ({ row }) => {
          const isSelf = selfUserUuid
            ? row.original.user_uuid === selfUserUuid
            : selfContactId != null && row.original.contact_id === selfContactId;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <span style={{ display: 'grid', lineHeight: 1.3 }}>
                <span style={{ fontWeight: 500 }}>{row.original.contact_name || '—'}</span>
                {row.original.contact_email && (
                  <span style={{ color: 'var(--vn-fg-muted)', fontSize: 12 }}>
                    {row.original.contact_email}
                  </span>
                )}
              </span>
              {isSelf && (
                <Badge tone="info" appearance="subtle" size="sm">
                  You
                </Badge>
              )}
            </span>
          );
        },
      },
      {
        id: 'role',
        header: 'Role',
        enableSorting: false,
        cell: ({ row }) => (
          <Badge tone={adminRoleTone(row.original.role)} appearance="subtle" size="sm">
            {titleCase(row.original.role || 'admin')}
          </Badge>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label={`Actions for ${row.original.contact_name}`}>
                  <MoreHorizontal size={16} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  tone="danger"
                  disabled={isLastAdmin}
                  onSelect={() => setRemoving(row.original)}
                >
                  Remove administrator
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      });
    }
    return cols;
  }, [canManage, selfUserUuid, selfContactId, isLastAdmin]);

  return (
    <>
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title="Administrators"
        description="People with administrative access to your organization."
        actions={
          canManage ? (
            <Button variant="primary" startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
              Add administrator
            </Button>
          ) : undefined
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search administrators…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        }
      />

      {isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 44 }} />
            ))}
          </div>
        ) : isError ? (
          <EmptyState icon={<ShieldCheck size={28} />} title="Couldn't load administrators" description="Something went wrong. Please try again." />
        ) : rows.length === 0 ? (
          q ? (
            <EmptyState icon={<Search size={28} />} title="No matching administrators" description={`No administrators match “${search.trim()}”.`} />
          ) : (
            <EmptyState
              icon={<ShieldCheck size={28} />}
              title="No administrators"
              description={canManage ? 'Add a contact with a login as an administrator.' : 'No administrators have been assigned.'}
              actions={
                canManage ? (
                  <Button startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
                    Add administrator
                  </Button>
                ) : undefined
              }
            />
          )
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.id)}
            footer={
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                {`${admins.length} administrator${admins.length === 1 ? '' : 's'}`}
              </span>
            }
          />
        )}

      <AddAdminDialog open={addOpen} onOpenChange={setAddOpen} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Remove administrator?"
        description={`“${removing?.contact_name ?? 'This administrator'}” will lose administrative access to your organization.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.user_uuid)}
      />
    </>
  );
}
